import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useRequest from '@hooks/useRequest';
import useClient from '@hooks/useClient';
import {
  STORAGE_ACTION,
  sessionStorage,
  authStorageClear,
  clearStorageAll
} from '@utils/storage';
import MyAccountSideMenuPresenter from './MyAccountSideMenuPresenter';
import ImageCrop from '@components/ImageCrop';
import { useSnackbar } from '@components/Provider';
import {
  CapaErrorCode,
  CapaQuotationUserFileType,
  UpdateQuotationUserInput,
  useUpdateQuotationUserMutation
} from '@generated/graphql';
import _ from 'lodash';
import { MyAccountContext } from '../../MyAccountContainer';
import ProfileSettingDialog from '../Dialog/ProfileSettingDialog/ProfileSettingDialog';
import useFiles from '@hooks/useFiles';

export const MyAccountSideMenuContainer: React.FC = () => {
  const history = useHistory();
  const inputRef = useRef<HTMLInputElement>(null);
  const context = React.useContext(MyAccountContext);
  //state
  const [open, setOpen] = useState(false);
  const [pictureDialogOpen, setPictureDialogOpen] = useState(false);
  const [, setIsLoading] = useState(false);
  const [cropState, setCropState] = useState({
    isOpen: false,
    file_key: '',
    file_name: '',
    aspect: 10 / 10
  });

  //custom hooks
  const { enqueueSnackbar } = useSnackbar();
  const { urlParam }: { urlParam: string } = useParams();
  const { onInitClient } = useClient();
  const { onInitRfqState } = useRequest();
  const { onUploadFiles } = useFiles();

  //mutation
  const [updateQuotationUser] = useUpdateQuotationUserMutation();

  //var
  const tempToken = localStorage.getItem(STORAGE_ACTION.TEMP_TOKEN);

  const fileList =
    context.data?.files && context.data.files.length > 0
      ? context.data.files
          .filter((v) => v.fileType !== CapaQuotationUserFileType.Profile)
          .map((e) => ({
            fileId: e.fileId ?? '',
            fileType: e.fileType ?? ''
          }))
      : [];

  useEffect(() => {
    if (urlParam === 'review') {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [urlParam, open]);

  const onUpdateProfileImage = async (
    param: Pick<UpdateQuotationUserInput, 'files'>
  ) => {
    let result: boolean = false;
    await updateQuotationUser({
      variables: {
        input: {
          files: param.files
        }
      }
    })
      .then(({ data }) => {
        const successUpdateProfileImage =
          data?.updateQuotationUser.result === CapaErrorCode.Success;
        if (successUpdateProfileImage) {
          context.getClientQueryRefetch && context.getClientQueryRefetch();
          if (pictureDialogOpen) {
            setPictureDialogOpen(false);
          }
          result = true;
        } else {
          result = false;
        }
      })
      .catch((err) => {
        //error
        result = false;
      });

    return result;
  };

  const handleLogout = () => {
    if (tempToken) {
      sessionStorage.removeItem(STORAGE_ACTION.TEMP_TOKEN);
    }
    onInitRfqState();
    authStorageClear();
    clearStorageAll();
    onInitClient();
    history.push('/login');
  };

  const onChangeCurrentTab = (index: number) => {
    if (index === 0) {
      //프로필 관리
      history.push('/mypage/profile');
    }
    if (index === 1) {
      //알림 설정
      history.push('/mypage/notification');
    }

    if (index === 2) {
      history.push('/mypage/review');
    }
  };

  const onClose = () => {
    setCropState({ ...cropState, isOpen: false, file_key: '', file_name: '' });
  };

  const onCropComplete = async (file: { file_name: string; blob: Blob }) => {
    if (file.blob) {
      setIsLoading(true);
      let blob: any = file.blob;
      blob.lastModifiedDate = new Date();
      blob.name = file.file_name;
      const { files, error } = await onUploadFiles([blob]);

      setIsLoading(false);
      if (!error) {
        const reqFiles: any = [
          ...fileList,
          {
            fileId: files.length !== 0 ? files[0].fileId : '',
            fileType: CapaQuotationUserFileType.Profile
          }
        ];

        await onUpdateProfileImage({ files: reqFiles })
          .then((result) => {
            if (result) {
              enqueueSnackbar('프로필 사진이 변경되었습니다.', {
                variant: 'success'
              });
            } else {
              enqueueSnackbar('잠시 후 다시 해주세요..', { variant: 'error' });
            }
          })
          .catch((err) => {
            enqueueSnackbar('잠시 후 다시 해주세요..', { variant: 'error' });
          });
        onClose();

        return true;
      }
      onClose();
      return false;
    }

    return false;
  };

  const onUpload = async (data: Array<File>) => {
    if (data.length < 1) {
      return;
    }

    setIsLoading(true);
    const { error, files } = await onUploadFiles(data);

    setIsLoading(false);
    if (error) return;
    setCropState({
      ...cropState,
      isOpen: true,
      file_key: files[0].fileId,
      file_name: files[0].fileName
    });
  };

  const onDelete = async () => {
    await onUpdateProfileImage({
      files: []
    })
      .then((result) => {
        if (result) {
          enqueueSnackbar('프로필 사진이 변경되었습니다.', {
            variant: 'success'
          });
        } else {
          enqueueSnackbar('잠시 후 다시 해주세요..', { variant: 'error' });
        }
      })
      .catch((err) => {
        enqueueSnackbar('잠시 후 다시 해주세요..', { variant: 'error' });
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const preventDoubleClick = useCallback(_.debounce(onDelete, 500), [onDelete]);

  return (
    <React.Fragment>
      <ProfileSettingDialog
        open={pictureDialogOpen}
        onClose={() => setPictureDialogOpen(false)}
        onUploadPicture={() => {
          if (inputRef && inputRef.current) {
            inputRef.current.click();
          }
        }}
        onDelete={preventDoubleClick}
      />
      <ImageCrop
        state={cropState}
        onClose={onClose}
        onSubmit={onCropComplete}
        onUpdateProfileImage={onUpdateProfileImage}
      />
      <MyAccountSideMenuPresenter
        onOpenPictureDialog={() => setPictureDialogOpen(true)}
        onChangeCurrentTab={onChangeCurrentTab}
        handleLogout={handleLogout}
        onUpload={onUpload}
        onUpdateProfileImage={onUpdateProfileImage}
        open={open}
        inputRef={inputRef}
      />
    </React.Fragment>
  );
};

export default MyAccountSideMenuContainer;
